import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from 'vexflow';

export const ScoreViewModal = ({ isOpen, onClose, tracks, timeSignature, tempo }) => {
  const scoreRef = useRef(null);
  
  useEffect(() => {
    if (!isOpen || !scoreRef.current) return;
    scoreRef.current.innerHTML = '';
    
    const allNotes = [];
    tracks.forEach(track => {
      track.notes.forEach(note => allNotes.push({ ...note, trackName: track.name }));
    });
    
    if (allNotes.length === 0) {
      scoreRef.current.innerHTML = '<p class="text-gray-400 text-center py-8">No notes.</p>';
      return;
    }
    
    allNotes.sort((a, b) => (a.measure * 4 + (a.beat || 0)) - (b.measure * 4 + (b.beat || 0)));
    
    const renderer = new Renderer(scoreRef.current, Renderer.Backends.SVG);
    renderer.resize(Math.max(800, allNotes.length * 50), 180);
    const context = renderer.getContext();
    
    const [beatsPerMeasure] = timeSignature.split('/').map(Number);
    const measureGroups = {};
    allNotes.forEach(note => {
      if (!measureGroups[note.measure]) measureGroups[note.measure] = [];
      measureGroups[note.measure].push(note);
    });
    
    let xPos = 10;
    Object.keys(measureGroups).sort((a, b) => a - b).forEach((m, idx) => {
      const stave = new Stave(xPos, 30, 180);
      if (idx === 0) stave.addClef('treble').addTimeSignature(timeSignature);
      stave.setContext(context).draw();
      
      const vexNotes = measureGroups[m].map(note => {
        const pitch = note.pitch.replace('/', '');
        const noteName = pitch.replace(/\d/, '').toLowerCase();
        const octave = pitch.match(/\d/)?.[0] || '4';
        const dur = note.duration === 'w' ? 'w' : note.duration === 'h' ? 'h' : note.duration === 'q' ? 'q' : '8';
        const sn = new StaveNote({ keys: [`${noteName}/${octave}`], duration: dur });
        if (noteName.includes('#')) sn.addModifier(new Accidental('#'), 0);
        return sn;
      });
      
      if (vexNotes.length > 0) {
        try {
          const voice = new Voice({ num_beats: beatsPerMeasure, beat_value: 4 }).setStrict(false);
          voice.addTickables(vexNotes);
          new Formatter().joinVoices([voice]).format([voice], 140);
          voice.draw(context, stave);
        } catch (e) { console.warn('VexFlow:', e); }
      }
      xPos += 180;
    });
  }, [isOpen, tracks, timeSignature]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Sheet Music</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-4">
              <p className="text-gray-500 text-sm">{tempo} BPM - {timeSignature}</p>
            </div>
            <div ref={scoreRef} className="overflow-x-auto" />
          </div>
        </div>
        <div className="p-4 border-t bg-gray-100 flex justify-end gap-3">
          <button onClick={() => window.print()} className="px-4 py-2 bg-purple-600 text-white rounded-lg">Print</button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
};

export default ScoreViewModal;
